import { useState, useEffect } from 'react';
import { auth } from '../lib/firebase/config';

export default function VerifyEmail() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleResendEmail = async () => {
    try {
      setIsLoading(true);
      setError('');
      setIsSuccess(false);
      const user = auth.currentUser;
      if (!user) {
        console.error('[VerifyEmail] No authenticated user found. Redirecting to login.');
        window.location.href = '/login';
        return;
      }
      console.log('[VerifyEmail] Sending verification email for:', user.email);
      // Call Mailjet Cloud Function
      const response = await fetch('https://us-central1-orrekie-platform.cloudfunctions.net/sendVerificationEmail', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: user.email,
          displayName: user.displayName || user.email.split('@')[0]
        })
      });
      const data = await response.json();
      console.log('[VerifyEmail] Mailjet response:', data);
      if (data.success) {
        setIsSuccess(true);
        setCountdown(60); // Start 60-second countdown
        console.log('[VerifyEmail] Verification email sent successfully.');
      } else {
        setError(data.message || 'Failed to send verification email.');
        console.error('[VerifyEmail] Failed to send verification email:', data.message);
      }
    } catch (err) {
      setError(err.message || 'An error occurred. Please try again.');
      console.error('[VerifyEmail] Mailjet fetch error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Verify your email</h2>

      <div className="rounded-md bg-blue-50 p-4 mb-6">
        <div className="flex">
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">
              Verification email sent
            </h3>
            <div className="mt-2 text-sm text-blue-700">
              <p>
                We've sent a verification email to your email address. Please check
                your inbox and click the verification link to continue.
              </p>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-4 mb-6">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">{error}</h3>
            </div>
          </div>
        </div>
      )}

      {isSuccess && (
        <div className="rounded-md bg-green-50 p-4 mb-6">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">
                Verification email resent
              </h3>
              <div className="mt-2 text-sm text-green-700">
                <p>
                  We've sent a new verification email. Please check your inbox.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-6">
        <div>
          <button
            onClick={handleResendEmail}
            disabled={isLoading || countdown > 0}
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orrekie-600 hover:bg-orrekie-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orrekie-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading
              ? 'Sending...'
              : countdown > 0
                ? `Resend in ${countdown}s`
                : 'Resend verification email'}
          </button>
        </div>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-gray-500">or</span>
          </div>
        </div>

        <div>
          <a
            href="/login"
            className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orrekie-500"
          >
            Return to sign in
          </a>
        </div>
      </div>

      <div className="mt-6 text-center text-sm text-gray-600">
        <p>
          Didn't receive the email? Check your spam folder or{' '}
          <button
            onClick={handleResendEmail}
            disabled={isLoading || countdown > 0}
            className="font-medium text-orrekie-600 hover:text-orrekie-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            try another email address
          </button>
        </p>
      </div>
    </div>
  );
} 